import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Grid, IconButton, TextField, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ViewModule, ViewList } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import config from '../config/config'; // Import configuration

const ProjectAssets = ({ projectId }) => {
  const [assets, setAssets] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // Set default view mode to 'list'
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('assetName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${config.apiBaseUrl}/api/assets/project/${projectId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        setAssets(response.data);
      } catch (error) {
        console.error('Error fetching assets:', error);
      }
    };

    fetchAssets();
  }, [projectId]);

  const toggleViewMode = () => {
    setViewMode((prevMode) => (prevMode === 'grid' ? 'list' : 'grid'));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedAssets = assets
    .filter((asset) =>
      asset.assetName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (typeof a[orderBy] === 'string') {
        return order === 'asc'
          ? a[orderBy].localeCompare(b[orderBy])
          : b[orderBy].localeCompare(a[orderBy]);
      }
      if (typeof a[orderBy] === 'number') {
        return order === 'asc' ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
      }
      return 0;
    });

  return (
    <Accordion>
      <AccordionDetails>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            label="Search Assets"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ marginRight: 2 }}
          />
          <IconButton onClick={toggleViewMode}>
            {viewMode === 'grid' ? <ViewList /> : <ViewModule />}
          </IconButton>
        </Box>
        {viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {sortedAssets.map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asset.id}>
                <Box border={1} borderRadius={4} p={2}>
                  <Typography variant="h6">{asset.assetName}</Typography>
                  <Typography variant="body2">Type: {asset.assetType}</Typography>
                  <Typography variant="body2">Category: {asset.category}</Typography>
                  <Typography variant="body2">Value: {asset.value}</Typography>
                  <Typography variant="body2">Location: {asset.location.locationName}</Typography>
                  <Typography variant="body2">Acquisition Date: {asset.acquisitionDate}</Typography>
                  <Typography variant="body2">Supplier: {asset.supplier.supplierName}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'assetName'}
                        direction={orderBy === 'assetName' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'assetName')}
                      >
                        Asset Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'assetType'}
                        direction={orderBy === 'assetType' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'assetType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'category'}
                        direction={orderBy === 'category' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'category')}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'value'}
                        direction={orderBy === 'value' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'value')}
                      >
                        Value
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'location'}
                        direction={orderBy === 'location' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'location')}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'acquisitionDate'}
                        direction={orderBy === 'acquisitionDate' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'acquisitionDate')}
                      >
                        Acquisition Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'supplier'}
                        direction={orderBy === 'supplier' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'supplier')}
                      >
                        Supplier
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAssets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset, index) => (
                    <TableRow
                      key={asset.id}
                      sx={{
                        cursor: 'pointer',
                        '&:nth-of-type(odd)': {
                          backgroundColor: '#f9f9f9',
                        },
                        '&:hover': {
                          backgroundColor: '#e0e0e0 !important',
                        },
                      }}
                    >
                      <TableCell>{asset.assetName}</TableCell>
                      <TableCell>{asset.assetType}</TableCell>
                      <TableCell>{asset.category}</TableCell>
                      <TableCell>{asset.value}</TableCell>
                      <TableCell>{asset.location.locationName}</TableCell>
                      <TableCell>{asset.acquisitionDate}</TableCell>
                      <TableCell>{asset.supplier.supplierName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={sortedAssets.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default ProjectAssets;